// SiteBatch types — mirror the Go backend's site-batch-deploy schema.
// Keep this file in sync with services/smartparking-manage-backend/src/features/parking/batch/model/entity/batch.go

/** Step status reported by the backend for each of the 8 services in a batch. */
export type StepStatus =
  | 'waiting'
  | 'dispatching'
  | 'running'
  | 'success'
  | 'failed'
  | 'timeout'
  | 'skipped';

/** Batch lifecycle. `completed` means every step has reached a terminal state — successes and failures are aggregated separately. */
export type BatchStatus = 'pending' | 'running' | 'completed' | 'cancelled';

/**
 * BatchStep is one row in the progress modal.
 * Maps 1:1 to a `deployment_results` row with batch_id set.
 */
export interface BatchStep {
  index: number;
  appname: string;
  label: string;
  status: StepStatus;
  deployment_id?: number;
  dispatched_at?: string;
  finished_at?: string;
  duration_sec?: number;
  failure_reason?: string;
  /**
   * deploy-failure-github-link: GitHub Actions workflow_run id.
   * null/undefined 이면 아직 dispatch 전이거나 dispatch 자체 실패 케이스.
   * 존재 시 UI 에서 `buildRunURL(id)` 로 링크 생성.
   */
  github_run_id?: number;
}

/** Full aggregate returned by the Start / Get endpoints. */
export interface SiteBatch {
  batch_id: number;
  site: string;
  status: BatchStatus;
  current_step: number;
  total_steps: number;
  success_count: number;
  failed_count: number;
  started_at: string;
  finished_at?: string | null;
  steps: BatchStep[];
}

/** Raw backend envelope shape — matches `BatchView` + echo JSON wrapper. */
export interface BatchViewResponse {
  success: boolean;
  message?: string;
  data: {
    batch: SiteBatch;
    steps: BatchStep[];
  };
}

/**
 * Per-service deploy config — mirrors backend `entity.ServiceDeployConfig`.
 *
 * `appname` 만 필수이고, `deploy_mode` (build|reuse) 와 `target_version` 은
 * deploy-service-mode-select feature 용. deploy-service-selection 단독 사용 시
 * 선택된 서비스의 appname 만 전송하면 백엔드가 `build` 모드로 취급한다.
 */
export interface ServiceDeployConfig {
  appname: string;
  deploy_mode?: 'build' | 'reuse';
  target_version?: string;
}

export interface StartBatchRequest {
  site: string;
  /**
   * 배포 대상. 'company'(생략 시 기본) = 사내 테스트 서버(company-deploy.yaml),
   * 'remote' = 사내 이미지를 현장 통합서버로 승격(site-deploy.yaml) — 모든 서비스가
   * reuse + target_version 이어야 하고 target_device_id 가 필요하다.
   */
  environment?: 'company' | 'remote';
  /** remote 전용: 이 현장의 통합서버(현장 장비 integration-server) id. */
  target_device_id?: number;
  /**
   * 배포할 서비스 config 목록. 비어있거나 생략하면 백엔드 기본값인
   * 8개 서비스 전부가 `build` 모드로 배포된다 (backward compatible).
   * 순서는 `SITE_BATCH_SERVICE_CATALOG` 기준으로 백엔드가 재정렬한다.
   */
  services?: ServiceDeployConfig[];
}

export interface StartBatchResponse extends BatchViewResponse {}

export interface RetryStepRequest {
  step_index: number;
}

export interface RetryStepResponse {
  success: boolean;
  message?: string;
  data: {
    batch_id: number;
    step_index: number;
    new_deployment_id: number;
    status: StepStatus;
  };
}

export interface ActiveBatchResponse {
  success: boolean;
  data: SiteBatch | null;
}

/**
 * RunningSite is the read model for the "현재 구동 중" banner.
 * A site is considered 구동 중 when its latest *completed* batch has a
 * successful router (step_index=1) step — the router gateway must be
 * alive for the site to be considered serving traffic.
 */
export interface RunningSite {
  site: string;
  batch_id: number;
  success_count: number;
  failed_count: number;
  total_steps: number;
  finished_at?: string | null;
  router_deployment_id: number;
}

/**
 * Terminal statuses — UI stops polling once the batch enters one of these.
 * Retries on individual steps do NOT reopen the batch.
 */
export const TERMINAL_BATCH_STATUSES: ReadonlyArray<BatchStatus> = ['completed', 'cancelled'];

/** True when the step is in a final state (UI shows no spinner). */
export function isTerminalStep(s: StepStatus): boolean {
  return s === 'success' || s === 'failed' || s === 'timeout' || s === 'skipped';
}

/** True when the batch is still making progress. */
export function isBatchRunning(s: BatchStatus): boolean {
  return s === 'pending' || s === 'running';
}

/**
 * Site-batch 의 8개 서비스 카탈로그 — backend `serviceOrder.go` 와 1:1 매칭.
 * UI 용 label 포함. 서버가 아직 생성하지 않은 step 행을 "대기" 상태로
 * 프레젠테이션할 때 기준 데이터로 사용된다.
 *
 * 주의: 서버 쪽 순서/이름이 바뀌면 반드시 이 리스트도 맞춰야 한다.
 */
export const SITE_BATCH_SERVICE_CATALOG: ReadonlyArray<{
  index: number;
  appname: string;
  label: string;
}> = [
  { index: 1, appname: 'smartparking-router', label: 'Router' },
  { index: 2, appname: 'lights', label: 'Lights' },
  { index: 3, appname: 'ht-location', label: 'HT Location' },
  { index: 4, appname: 'admin-client', label: 'Admin Client' },
  { index: 5, appname: 'smartparking-induce-worker', label: 'Induce Worker' },
  { index: 6, appname: 'smartparking-induce', label: 'Induce' },
  { index: 7, appname: 'smartparking-rtsp-back', label: 'RTSP Back' },
  { index: 8, appname: 'smartparking-map', label: 'Map' },
];

/**
 * Merge the fixed 8-service catalog with actual step rows from the
 * backend, synthesizing placeholder "waiting" rows for step indices that
 * don't yet exist in the DB (the backend only creates a step row at
 * dispatch time).
 *
 * Ensures the UI always displays exactly 8 rows in canonical order even
 * when the batch is still on step 1.
 */
export function mergeStepsWithCatalog(steps: BatchStep[]): BatchStep[] {
  // deploy-service-selection 이후 backend 가 StartBatch 시점에 선택된 N 개
  // step row 를 모두 pre-create 하므로 전체 카탈로그(8개)로 padding 하지 않는다.
  // retries 처리(같은 step_index 에 여러 row) 만 dedupe 하고 index 정렬 후 반환.
  const byIndex = new Map<number, BatchStep>();
  steps.forEach((s) => {
    const existing = byIndex.get(s.index);
    if (!existing) {
      byIndex.set(s.index, s);
      return;
    }
    const existingId = existing.deployment_id ?? 0;
    const candId = s.deployment_id ?? 0;
    if (candId > existingId) byIndex.set(s.index, s);
  });

  return Array.from(byIndex.values()).sort((a, b) => a.index - b.index);
}

/**
 * 현장별 자정 자동 배포 — GET/PUT /v0.1/parking/:projectId/deployments/auto-deploy.
 * 매일 00:00(KST) 백엔드가 현장 통합서버를 다시 읽어, 고른 서비스 중 버전이 다른 것만
 * 사내 이미지를 승격한다(현장 배포 히스토리에 실행자 "자동 배포(자정)" 로 남는다).
 */
export interface AutoDeployService {
  appname: string;
  /** 사내 서버에 있는 vX.Y.Z */
  target_version: string;
}

/** same 이미 같음 · started 배포 중(끝 확인 전) · deployed 완료 · failed 실패 · rolled_back 되돌림 · skipped 건너뜀 */
export type AutoDeployOutcome = 'same' | 'started' | 'deployed' | 'failed' | 'rolled_back' | 'skipped';

export interface AutoDeployResult {
  appname: string;
  target_version: string;
  running_version?: string;
  outcome: AutoDeployOutcome;
  reason?: string;
}

export interface AutoDeploySetting {
  project_id: number;
  enabled: boolean;
  /** 배포할 통합서버(site_network_devices.id). 0 = 미지정 */
  target_device_id: number;
  services: AutoDeployService[];
  last_run_at: string | null;
  last_batch_id: number | null;
  last_results: AutoDeployResult[] | null;
  updated_by: string;
  updated_at: string;
}

export interface SaveAutoDeployRequest {
  enabled: boolean;
  target_device_id: number;
  services: AutoDeployService[];
}
